import { appSdkImportMap, type AppFrameConfiguration } from './app-sdk.ts'
import { inlineScript } from './inline-script.ts'

/** Host-owned srcdoc shell. Because the document is srcdoc, the host passes its own origin
 * explicitly. Mount with sandbox="allow-scripts allow-downloads allow-pointer-lock".
 */
export const composeAppFrame = (config: AppFrameConfiguration, bootstrap: string): string => {
  const parent = new URL(config.expectedParentOrigin)
  if (
    !['https:', 'http:'].includes(parent.protocol) ||
    parent.origin !== config.expectedParentOrigin
  ) {
    throw new Error('App frame requires an explicit canonical parent origin')
  }
  const map = appSdkImportMap(config.graph, config.assetBase)
  const scripts = [...new Set(Object.values(map.imports))].join(' ')
  const styles = config.graph.styles.map((hash) => `${config.assetBase}${hash}.css`).join(' ')
  // CSP matches paths, not query strings; the CDN accepts query variants of hash-named assets.
  // SDK-generated URLs stay canonical and integrity-checked (see the workflows-v2 README).
  const csp = `default-src 'none'; script-src 'unsafe-inline' blob: ${scripts}; style-src 'unsafe-inline' ${styles}; img-src data:; font-src data:; connect-src 'none'; worker-src 'none'; frame-src 'none'; form-action 'none'; base-uri 'none'`
  const script = inlineScript(
    `const __APP_FRAME_CONFIGURATION__ = ${JSON.stringify(config)};\n${bootstrap}`,
  )
  // base.css scopes every rule to .arp-root. Guest apps are always ARP without any legacy code, so the class goes on
  // body: a narrower container would leave body-level content (portals, library popups) unstyled.
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${csp}"><title>App sandbox</title><style>html,body,#root{background-color:var(--page-background);height:100%;margin:0}</style></head><body class="arp-root"><div id="root"></div><script>${script}</script></body></html>`
}
